import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/imageUrl';
import './BannerManagement.css';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api`;
// Empty = use main server /api/upload-image (production). Local dev: main server also handles it.
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_UPLOAD_URL || '';

const BannerManagement = () => {
  const { t } = useTranslation('navigation');
  const [activeTab, setActiveTab] = useState('website');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    type: 'website',
    title: '',
    link_url: '',
    unit_id: '',
    language: '',
    active: true,
    order: 0,
    image: null,
    imagePreview: null
  });
  const [unitsByLevel, setUnitsByLevel] = useState([]); // { level, units: [{ id, unit_number, title }] }

  // Languages for Unit Banners (same as course management)
  const UNIT_BANNER_LANGUAGES = [
    { value: '', label: 'Default (all languages)' },
    { value: 'English', label: 'English' },
    { value: 'German', label: 'German' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'Italian', label: 'Italian' },
    { value: 'French', label: 'French' }
  ];

  // German 3rd year High School unit titles (same as user dashboard)
  const GERMAN_THIRD_YEAR_UNIT_TITLES = {
    1: 'Die Umweltprobleme',
    2: 'Die Massenmedien und Kommunikationsmittel',
    3: 'Jugendprobleme',
    4: 'Die deutschsprachigen Länder',
    5: 'Künstlerleben',
    6: 'Der technische Fortschritt'
  };

  // Fetch banners based on active tab
  useEffect(() => {
    fetchBanners();
  }, [activeTab]);

  // Fetch units grouped by level (for Unit Banners dropdown)
  useEffect(() => {
    if (activeTab !== 'unit_card_placeholder') return;
    fetch(`${API_BASE_URL}/explore/units/list`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.groups)) setUnitsByLevel(data.groups);
        else setUnitsByLevel([]);
      })
      .catch(() => setUnitsByLevel([]));
  }, [activeTab]);

  // Filter units by selected language: German/Spanish/Italian = only High School 2nd & 3rd year; others = all
  const unitsByLevelFilteredByLanguage = useMemo(() => {
    if (!Array.isArray(unitsByLevel) || unitsByLevel.length === 0) return [];
    const lang = (formData.language || '').trim().toLowerCase();
    if (!lang || lang === 'english' || lang === 'french') return unitsByLevel;
    if (lang === 'german' || lang === 'spanish' || lang === 'italian') {
      return unitsByLevel.filter((group) => {
        const level = (group.level || '').toLowerCase();
        const isHighSchool = level.includes('high school');
        const isSecondYear = level.includes('second year');
        const isThirdYear = level.includes('third year');
        return isHighSchool && (isSecondYear || isThirdYear);
      });
    }
    return unitsByLevel;
  }, [unitsByLevel, formData.language]);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const typeForApi = activeTab === 'logo' ? 'website' : activeTab;
      const response = await fetch(`${API_BASE_URL}/banners?type=${typeForApi}&active=false`);
      const data = await response.json();
      if (data.success) {
        let list = data.banners || [];
        if (activeTab === 'logo') {
          list = list.filter((b) => (b.title || '').toLowerCase().includes('logo'));
        }
        setBanners(list);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const mappedType = tab === 'logo' ? 'website' : tab;
    setFormData(prev => ({ ...prev, type: mappedType }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // When language changes, clear unit_id if it's no longer in the filtered list for that language
      if (name === 'language' && prev.unit_id) {
        const lang = (value || '').trim().toLowerCase();
        const filtered = lang === 'german' || lang === 'spanish' || lang === 'italian'
          ? (unitsByLevel || []).filter((g) => {
              const l = (g.level || '').toLowerCase();
              return l.includes('high school') && (l.includes('second year') || l.includes('third year'));
            })
          : unitsByLevel || [];
        const unitIds = new Set((filtered || []).flatMap((g) => (g.units || []).map((u) => String(u.id))));
        if (!unitIds.has(String(prev.unit_id))) next.unit_id = '';
      }
      return next;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image && !editingBanner) {
      alert('Please select an image');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('type', formData.type);
    formDataToSend.append('title', formData.title);
    if (formData.type === 'website' && formData.link_url) {
      formDataToSend.append('link_url', formData.link_url);
    }
    if (formData.type === 'unit_card_placeholder' && formData.unit_id !== undefined) {
      formDataToSend.append('unit_id', formData.unit_id);
    }
    if (formData.type === 'unit_card_placeholder' && formData.language !== undefined) {
      formDataToSend.append('language', formData.language);
    }
    formDataToSend.append('active', formData.active);
    formDataToSend.append('order', formData.order);
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    try {
      let response;
      if (editingBanner) {
        response = await fetch(`${API_BASE_URL}/banners/${editingBanner.id}`, {
          method: 'PUT',
          body: formDataToSend
        });
      } else {
        response = await fetch(`${API_BASE_URL}/banners`, {
          method: 'POST',
          body: formDataToSend
        });
      }

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok) {
        if (response.status === 413) {
          alert('Image is too large. Use an image under 5MB, or ask your host to increase the upload limit (Nginx: client_max_body_size).');
          return;
        }
        if (contentType.includes('application/json')) {
          const data = await response.json().catch(() => ({}));
          alert(data.message || `Error saving banner (${response.status})`);
        } else {
          alert(`Server error ${response.status}. If uploading an image, it may be too large — try a smaller image (under 2MB).`);
        }
        return;
      }
      const data = await response.json().catch(() => null);
      if (data && data.success) {
        setShowModal(false);
        resetForm();
        fetchBanners();
      } else {
        alert(data?.message || 'Error saving banner');
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      if (error.message && error.message.includes('JSON')) {
        alert('Server returned an error (possibly image too large). Try a smaller image (under 2MB).');
      } else {
        alert('Error saving banner');
      }
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      type: banner.type,
      title: banner.title || '',
      link_url: banner.link_url || '',
      unit_id: banner.unit_id != null ? String(banner.unit_id) : '',
      language: banner.language != null ? String(banner.language) : '',
      active: banner.active === 1 || banner.active === true,
      order: banner.order || 0,
      image: null,
      imagePreview: banner.image_url ? (getImageUrl(banner.image_url) || (IMAGE_SERVICE_URL ? `${IMAGE_SERVICE_URL}${banner.image_url.startsWith('/') ? '' : '/'}${banner.image_url}` : banner.image_url)) : null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchBanners();
      } else {
        alert(data.message || 'Error deleting banner');
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('Error deleting banner');
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      const response = await fetch(`${API_BASE_URL}/banners/${id}/active`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: !currentActive })
      });
      const data = await response.json();
      if (data.success) {
        fetchBanners();
      } else {
        alert(data.message || 'Error updating banner status');
      }
    } catch (error) {
      console.error('Error updating banner status:', error);
      alert('Error updating banner status');
    }
  };

  const resetForm = () => {
    setFormData({
      type: activeTab === 'logo' ? 'website' : activeTab,
      title: activeTab === 'logo' ? 'Logo' : '',
      link_url: '',
      unit_id: '',
      language: '',
      active: true,
      order: 0,
      image: null,
      imagePreview: null
    });
    setEditingBanner(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="banner-management">
      <div className="banner-header">
        <div>
          <h1>{t('bannerManagement.title')}</h1>
          <p>{t('bannerManagement.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <i className="material-icons">add</i>
          {t('bannerManagement.addNewBanner')}
        </button>
      </div>

      {/* Tabs */}
      <div className="banner-tabs">
        <button
          className={`tab-button ${activeTab === 'website' ? 'active' : ''}`}
          onClick={() => handleTabChange('website')}
        >
          {t('bannerManagement.tabs.websiteBanners')}
        </button>
        <button
          className={`tab-button ${activeTab === 'app_onboarding' ? 'active' : ''}`}
          onClick={() => handleTabChange('app_onboarding')}
        >
          {t('bannerManagement.tabs.appOnboardingBanners')}
        </button>
        <button
          className={`tab-button ${activeTab === 'unit_card_placeholder' ? 'active' : ''}`}
          onClick={() => handleTabChange('unit_card_placeholder')}
        >
          {t('bannerManagement.tabs.unitBanners')}
        </button>
        <button
          className={`tab-button ${activeTab === 'logo' ? 'active' : ''}`}
          onClick={() => handleTabChange('logo')}
        >
          {t('bannerManagement.tabs.logoBanners', 'Logo')}
        </button>
      </div>

      {/* Banners Grid */}
      {loading ? (
        <div className="loading-state">
          <i className="material-icons">hourglass_empty</i>
          <p>{t('bannerManagement.loading')}</p>
        </div>
      ) : banners.length === 0 ? (
        <div className="empty-state">
          <i className="material-icons">image</i>
          <p>{t('bannerManagement.noBanners')}</p>
          <button className="btn-primary" onClick={openAddModal}>
            {t('bannerManagement.addFirstBanner')}
          </button>
        </div>
      ) : (
        <div className="banners-grid">
          {banners.map((banner) => (
            <div key={banner.id} className="banner-card">
              <div className="banner-image-container">
                <img
                  src={getImageUrl(banner.image_url) || (IMAGE_SERVICE_URL ? `${IMAGE_SERVICE_URL}${banner.image_url.startsWith('/') ? '' : '/'}${banner.image_url}` : banner.image_url)}
                  alt={banner.title || 'Banner'}
                  onError={(e) => {
                    e.target.src = '/placeholder-banner.jpg';
                  }}
                />
                <div className={`banner-status ${banner.active ? 'active' : 'inactive'}`}>
                  {banner.active ? t('bannerManagement.active') : t('bannerManagement.inactive')}
                </div>
              </div>
              <div className="banner-info">
                <h3>{banner.title || t('bannerManagement.noTitle')}</h3>
                {banner.type === 'website' && banner.link_url && (
                  <p className="banner-link">
                    <i className="material-icons">link</i>
                    <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                      {banner.link_url}
                    </a>
                  </p>
                )}
                <div className="banner-meta">
                  <span>
                    <i className="material-icons">sort</i>
                    {t('bannerManagement.order')}: {banner.order}
                  </span>
                  {banner.type === 'unit_card_placeholder' && (
                    <>
                      {banner.unit_id != null && (
                        <span>
                          <i className="material-icons">school</i>
                          Unit ID: {banner.unit_id}
                        </span>
                      )}
                      <span>
                        <i className="material-icons">language</i>
                        {banner.language ? banner.language : 'Default (all languages)'}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="banner-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleToggleActive(banner.id, banner.active === 1 || banner.active === true)}
                  title={banner.active ? t('bannerManagement.deactivate') : t('bannerManagement.activate')}
                >
                  <i className="material-icons">
                    {banner.active ? 'visibility' : 'visibility_off'}
                  </i>
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleEdit(banner)}
                  title={t('bannerManagement.edit')}
                >
                  <i className="material-icons">edit</i>
                </button>
                <button
                  className="btn-icon btn-danger"
                  onClick={() => handleDelete(banner.id)}
                  title={t('bannerManagement.delete')}
                >
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingBanner
                  ? t('bannerManagement.editBanner')
                  : t('bannerManagement.addNewBanner')}
              </h2>
              <button className="btn-icon" onClick={closeModal}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="banner-form">
              <div className="form-group">
                <label>{t('bannerManagement.form.type')}</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  disabled={!!editingBanner}
                >
                  <option value="website">{t('bannerManagement.form.website')}</option>
                  <option value="app_onboarding">{t('bannerManagement.form.appOnboarding')}</option>
                  <option value="unit_card_placeholder">{t('bannerManagement.form.unitBanners')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('bannerManagement.form.image')} *</label>
                <div className="image-upload">
                  {formData.imagePreview ? (
                    <div className="image-preview">
                      <img src={formData.imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            image: null,
                            imagePreview: null
                          }));
                        }}
                      >
                        <i className="material-icons">close</i>
                      </button>
                    </div>
                  ) : (
                    <label className="upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        required={!editingBanner}
                      />
                      <div className="upload-placeholder">
                        <i className="material-icons">cloud_upload</i>
                        <p>{t('bannerManagement.form.chooseImage')}</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>{t('bannerManagement.form.title')}</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={t('bannerManagement.form.titlePlaceholder')}
                />
              </div>

              {formData.type === 'website' && (
                <div className="form-group">
                  <label>{t('bannerManagement.form.linkUrl')}</label>
                  <input
                    type="text"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    placeholder={t('bannerManagement.form.linkUrlPlaceholder')}
                  />
                </div>
              )}

              {formData.type === 'unit_card_placeholder' && (
                <>
                  <div className="form-group">
                    <label>Language</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                    >
                      {UNIT_BANNER_LANGUAGES.map((opt) => (
                        <option key={opt.value || 'default'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="form-hint">Choose which language this banner is for (e.g. German unit cards). Default = used when no language-specific banner exists.</p>
                  </div>
                  <div className="form-group">
                    <label>{t('bannerManagement.form.unit')}</label>
                    <select
                      name="unit_id"
                      value={formData.unit_id}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('bannerManagement.form.defaultForAllUnits')}</option>
                      {unitsByLevelFilteredByLanguage.map((group) => {
                        const level = (group.level || '').toLowerCase();
                        const isGermanThirdYear = formData.language && String(formData.language).trim().toLowerCase() === 'german' &&
                          level.includes('high school') && level.includes('third year');
                        return (
                          <optgroup key={group.level} label={group.level}>
                            {group.units.map((u) => {
                              const displayTitle = isGermanThirdYear && GERMAN_THIRD_YEAR_UNIT_TITLES[u.unit_number]
                                ? GERMAN_THIRD_YEAR_UNIT_TITLES[u.unit_number]
                                : (u.title || `Unit ${u.unit_number}`);
                              return (
                                <option key={u.id} value={u.id}>
                                  Unit {u.unit_number}: {displayTitle}
                                </option>
                              );
                            })}
                          </optgroup>
                        );
                      })}
                    </select>
                    <p className="form-hint">{t('bannerManagement.form.unitBannersHint')} Units shown match the selected language.</p>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>{t('bannerManagement.form.order')}</label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  <span>{t('bannerManagement.form.active')}</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  {t('bannerManagement.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {editingBanner ? t('bannerManagement.update') : t('bannerManagement.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerManagement;

